import { motion } from "framer-motion";
import { Crown, Building2, Users, GraduationCap, Home as HomeIcon, Check, X, Cpu, Bot, Workflow, BarChart3, Smartphone, MonitorSmartphone, Mail, Activity, LayoutGrid } from "lucide-react";

/* ---------- Stakeholder Hub (central OS + 5 connected roles) ---------- */

const roles = [
  { icon: Crown, label: "Principal", tone: "#6366F1" },
  { icon: Building2, label: "School Admin", tone: "#5B7CFA" },
  { icon: Users, label: "Teachers", tone: "#22C55E" },
  { icon: GraduationCap, label: "Students", tone: "#F5B93C" },
  { icon: HomeIcon, label: "Parents", tone: "#8B5CF6" },
];

export const StakeholderHub = () => {
  const cx = 50, cy = 50, R = 38;
  const nodes = roles.map((r, i) => {
    const a = (-90 + i * (360 / roles.length)) * (Math.PI / 180);
    return { ...r, x: cx + Math.cos(a) * R, y: cy + Math.sin(a) * R };
  });
  return (
    <div className="relative w-full max-w-xl mx-auto aspect-square" data-testid="stakeholder-hub">
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
        {nodes.map((n, i) => (
          <g key={i}>
            <line x1={cx} y1={cy} x2={n.x} y2={n.y} stroke="#D8DCE6" strokeWidth="0.5" />
            <motion.circle r="1" fill={n.tone}
              initial={{ cx: n.x, cy: n.y, opacity: 0 }}
              animate={{ cx: [n.x, cx], cy: [n.y, cy], opacity: [0, 1, 1, 0] }}
              transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }} />
          </g>
        ))}
      </svg>
      {/* center */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <div className="h-28 w-28 md:h-32 md:w-32 rounded-2xl bg-[#0E1117] flex flex-col items-center justify-center text-center shadow-[0_20px_50px_-16px_rgba(99,102,241,0.5)] border border-white/10">
          <span className="text-[#A5B4FC] text-[9px] font-mono-accent tracking-widest">PARTECON</span>
          <span className="text-white font-display font-semibold text-sm leading-tight mt-1">THE<br />SCHOOL OS</span>
          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#22C55E] animate-pulse-node" />
        </div>
      </div>
      {/* role nodes */}
      {nodes.map((n) => (
        <div key={n.label} className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${n.x}%`, top: `${n.y}%` }}>
          <div className="flex flex-col items-center gap-2">
            <div className="h-14 w-14 rounded-2xl bg-white border border-[#ECECE8] flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(16,17,23,0.15)]">
              <n.icon size={20} style={{ color: n.tone }} />
            </div>
            <span className="text-[11px] font-medium text-[#1A1D26] whitespace-nowrap">{n.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ---------- Ecosystem architecture grid ---------- */

const layers = [
  {
    tag: "Experiences", items: [
      { icon: Smartphone, label: "Teacher App" }, { icon: GraduationCap, label: "Student App" },
      { icon: HomeIcon, label: "Parent Experience" }, { icon: MonitorSmartphone, label: "Principal Dashboard" },
    ],
  },
  {
    tag: "Operations", items: [
      { icon: Building2, label: "School Administration" }, { icon: Crown, label: "Central Management" },
      { icon: BarChart3, label: "Analytics" }, { icon: Workflow, label: "Automation" },
    ],
  },
  {
    tag: "Intelligence & Infrastructure", items: [
      { icon: Bot, label: "AI Intelligence" }, { icon: Cpu, label: "Smart Hardware" },
    ],
  },
];

export const EcosystemArch = () => (
  <div className="space-y-4" data-testid="ecosystem-arch">
    {layers.map((layer, li) => (
      <motion.div key={layer.tag}
        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, delay: li * 0.1 }}
        className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="overline-light overline">{layer.tag}</span>
          {layer.roadmap && <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/15 text-white/50 font-mono-accent">ROADMAP</span>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {layer.items.map((it) => (
            <div key={it.label}
              className={`rounded-xl border p-4 flex items-center gap-3 ${layer.roadmap ? "border-white/8 bg-white/[0.02] opacity-70" : "border-white/10 bg-white/5"}`}>
              <span className="h-9 w-9 rounded-lg bg-[#6366F1]/15 flex items-center justify-center shrink-0">
                <it.icon size={17} className="text-[#A5B4FC]" />
              </span>
              <span className="text-[13px] text-white/85 font-medium leading-tight">{it.label}</span>
            </div>
          ))}
        </div>
      </motion.div>
    ))}
    <p className="text-center text-white/40 text-xs font-mono-accent">All connected through the PARTECON School OS</p>
  </div>
);

/* ---------- Comparison table ---------- */

const compareRows = [
  ["Records information", "Operates the school in real time"],
  ["Administrative focus", "Whole-ecosystem operations"],
  ["Reactive reporting", "Proactive operational intelligence"],
  ["Fragmented workflows", "One connected, governed ecosystem"],
  ["Manual follow-up", "Automation & exception alerts"],
  ["Static data store", "AI-ready operating infrastructure"],
];

export const ComparisonTable = () => (
  <div className="grid md:grid-cols-2 gap-4 md:gap-6" data-testid="comparison-table">
    <div className="rounded-2xl border border-[#ECECE8] bg-white/60 p-6 md:p-8">
      <div className="flex items-center gap-2 mb-6">
        <span className="text-[13px] font-mono-accent uppercase tracking-widest text-[#6B7280]">Traditional ERP / SIS</span>
      </div>
      <ul className="space-y-4">
        {compareRows.map((r) => (
          <li key={r[0]} className="flex items-center gap-3 text-[15px] text-[#6B7280]">
            <span className="h-5 w-5 rounded-full bg-[#F1F1EC] flex items-center justify-center shrink-0"><X size={12} className="text-[#9AA1AD]" /></span>
            {r[0]}
          </li>
        ))}
      </ul>
    </div>
    <div className="relative rounded-2xl bg-white p-6 md:p-8 shadow-[0_18px_50px_-18px_rgba(99,102,241,0.25)] border border-[#E4E4FB]">
      <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gradient-to-r from-[#6366F1] to-[#22C55E]" />
      <div className="flex items-center gap-2 mb-6">
        <span className="h-6 w-6 rounded-md bg-[#6366F1] flex items-center justify-center text-white text-[11px] font-bold font-display">P</span>
        <span className="text-[13px] font-mono-accent uppercase tracking-widest text-[#4F46E5]">PARTECON — The School OS</span>
      </div>
      <ul className="space-y-4">
        {compareRows.map((r) => (
          <li key={r[1]} className="flex items-center gap-3 text-[15px] text-[#1A1D26] font-medium">
            <span className="h-5 w-5 rounded-full bg-[#DCFCE7] flex items-center justify-center shrink-0"><Check size={12} className="text-[#16A34A]" /></span>
            {r[1]}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

/* ---------- Evolution timeline ---------- */

const stages = [
  { label: "Digital Records", now: true },
  { label: "Connected Systems", now: true },
  { label: "Real-Time Operations", now: true },
  { label: "Automation", now: true },
  { label: "AI Intelligence", now: true },
  { label: "Smart School Infrastructure", now: true },
];

export const EvolutionTimeline = () => (
  <div className="relative" data-testid="evolution-timeline">
    <div className="absolute left-[11px] md:left-0 md:top-[11px] top-0 md:h-px md:w-full h-full w-px bg-gradient-to-b md:bg-gradient-to-r from-[#6366F1] via-[#8B5CF6] to-[#22C55E]" />
    <div className="grid md:grid-cols-6 gap-8 md:gap-4 relative">
      {stages.map((s, i) => (
        <motion.div key={s.label}
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.5, delay: i * 0.08 }}
          className="relative pl-9 md:pl-0 md:pt-9">
          <span className={`absolute left-0 top-0 md:top-0 h-[22px] w-[22px] rounded-full border-2 flex items-center justify-center ${
            s.now ? "border-[#6366F1] bg-[#6366F1]" : "border-white/20 bg-[#0E1117]"}`}>
            {s.now && <span className="h-2 w-2 rounded-full bg-white" />}
          </span>
          <div className={`text-[13px] font-medium leading-tight ${s.now ? "text-white" : "text-white/45"}`}>{s.label}</div>
          <div className={`text-[10px] font-mono-accent mt-1.5 ${s.now ? "text-[#22C55E]" : "text-white/30"}`}>{s.now ? "AVAILABLE" : "ROADMAP"}</div>
        </motion.div>
      ))}
    </div>
  </div>
);

/* ---------- Four Operating Pillars ---------- */

const pillars = [
  {
    icon: GraduationCap, tone: "#6366F1", label: "Academic Execution",
    summary: "How learning is delivered, captured and closed — every day.",
    items: ["Class execution", "Attendance", "Assignments & evaluation", "Examinations", "Lesson plans"],
    primitive: "EOC — the operational record of every class.",
  },
  {
    icon: Mail, tone: "#5B7CFA", label: "Communication",
    summary: "Structured, role-based communication for the whole school.",
    items: ["Role-based channels", "Notices & circulars", "Escalations & records", "Parent-teacher exchanges"],
    primitive: "PCC — one governed communication layer.",
  },
  {
    icon: Activity, tone: "#22C55E", label: "Intelligence",
    summary: "Daily signals turned into early, actionable insight.",
    items: ["Performance analytics", "Behaviour & engagement", "At-risk identification", "Trends & patterns"],
    primitive: "AHI — the analytical health of the school.",
  },
  {
    icon: LayoutGrid, tone: "#8B5CF6", label: "School Operations",
    summary: "The coordinating layer for daily school operations.",
    items: ["Scheduling & calendar", "Records & workflows", "Roles & permissions", "Institutional coordination"],
    primitive: "One governed operating layer — not many disconnected tools.",
  },
];

export const FourPillars = () => (
  <div className="grid md:grid-cols-2 gap-4" data-testid="four-pillars">
    {pillars.map((p, i) => (
      <motion.div
        key={p.label}
        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.55, delay: i * 0.06 }}
        className="glass rounded-2xl p-7 h-full flex flex-col"
        data-testid={`pillar-${p.label.split(" ")[0].toLowerCase()}`}
      >
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${p.tone}22` }}>
            <p.icon size={20} style={{ color: p.tone }} />
          </span>
          <h3 className="font-display font-semibold text-white text-[17px]">{p.label}</h3>
        </div>
        <p className="text-white/60 text-[14px] leading-relaxed mt-4">{p.summary}</p>
        <ul className="mt-5 grid grid-cols-2 gap-x-3 gap-y-2">
          {p.items.map((it) => (
            <li key={it} className="text-[13px] text-white/85 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: p.tone }} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
        <div className="mt-auto pt-5 border-t border-white/10 flex items-start gap-2">
          <span className="text-[10px] font-mono-accent uppercase tracking-widest text-white/40 shrink-0 mt-0.5">Primitive</span>
          <span className="text-[12px] text-[#A5B4FC] font-medium leading-snug">{p.primitive}</span>
        </div>
      </motion.div>
    ))}
  </div>
);

